import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { SOURCES, STATUSES } from "../lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, Line, Area, AreaChart,
} from "recharts";
import { TrendUp, TrendDown, Funnel, CheckCircle } from "@phosphor-icons/react";

function Metric({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
          {label}
        </div>
        {Icon && <Icon size={16} weight="regular" className={accent || "text-foreground/60"} />}
      </div>
      <div className="font-display text-4xl font-bold tracking-tight mt-3 tabular-nums">
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [attributionHealth, setAttributionHealth] = useState(null);
  const [attrCoverage, setAttrCoverage] = useState(null);
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filter, setFilter] = useState({ branch_id: "", agent_id: "", date_from: "", date_to: "" });

  const load = async () => {
    const params = {};
    Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
    const attrParams = {};
    if (filter.date_from) attrParams.date_from = filter.date_from;
    if (filter.date_to) attrParams.date_to = filter.date_to;
    const [a, b, ag, ah, ac] = await Promise.all([
      api.get("/analytics/overview", { params }),
      api.get("/branches"),
      api.get("/agents"),
      api.get("/analytics/attribution-health", { params: attrParams }).catch(() => ({ data: null })),
      api.get("/analytics/attribution-coverage-by-source", { params: attrParams }).catch(() => ({ data: null })),
    ]);
    setAnalytics(a.data);
    setBranches(b.data);
    setAgents(ag.data);
    setAttributionHealth(ah.data);
    setAttrCoverage(ac.data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const sourceData = useMemo(() => SOURCES.map((s) => ({
    name: s.short, full: s.label, value: analytics?.by_source?.[s.id] || 0,
  })), [analytics]);

  const statusData = useMemo(() => STATUSES.map((s) => ({
    name: s.label, value: analytics?.by_status?.[s.id] || 0, color: s.color,
  })).filter((x) => x.value > 0), [analytics]);

  const branchData = useMemo(() => branches.map((b) => ({
    name: b.name, value: analytics?.by_branch?.[b.id] || 0,
  })), [branches, analytics]);

  const agentData = useMemo(() => agents.map((a) => ({
    name: a.name, value: analytics?.by_agent?.[a.id] || 0,
  })), [agents, analytics]);

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Overview"
        title="Operations dashboard"
        description="Real-time view of lead flow across every source, branch and agent."
      />

      {/* Filter bar */}
      <div className="px-8 py-4 border-b border-border bg-white flex flex-wrap gap-3 items-center">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Filters</span>
        <select
          value={filter.branch_id}
          onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}
          data-testid="dashboard-filter-branch"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="">All branches</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select
          value={filter.agent_id}
          onChange={(e) => setFilter({ ...filter, agent_id: e.target.value })}
          data-testid="dashboard-filter-agent"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="">All agents</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <input
          type="date"
          value={filter.date_from}
          onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
          data-testid="dashboard-filter-from"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        />
        <span className="text-muted-foreground text-xs">→</span>
        <input
          type="date"
          value={filter.date_to}
          onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
          data-testid="dashboard-filter-to"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        />
        {(filter.branch_id || filter.agent_id || filter.date_from || filter.date_to) && (
          <button
            onClick={() => setFilter({ branch_id: "", agent_id: "", date_from: "", date_to: "" })}
            data-testid="dashboard-filter-clear"
            className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Metrics */}
      <div className="px-8 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Total Leads" value={analytics?.total ?? "—"} icon={Funnel} />
        <Metric label="Converted" value={analytics?.converted ?? "—"} icon={CheckCircle} accent="text-[#008A00]" />
        <Metric
          label="Open Pipeline"
          value={analytics?.open ?? "—"}
          icon={TrendUp}
          accent="text-primary"
          sub={analytics?.closed_other != null ? `${analytics.closed_other} closed (other)` : "Actionable leads only"}
        />
        <Metric
          label="Conversion Rate"
          value={`${analytics?.conversion_rate ?? 0}%`}
          icon={TrendDown}
          sub={`${analytics?.lost ?? 0} lost`}
        />
      </div>

      {/* Attribution health widget */}
      {attributionHealth && attributionHealth.total > 0 && (
        <div className="px-8 pb-6" data-testid="attribution-health-widget">
          <div className="border border-border bg-white p-5">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Attribution health</div>
                <h3 className="font-display text-lg font-semibold tracking-tight">
                  {attributionHealth.tagged_percent}% of leads carry attribution tags
                </h3>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {attributionHealth.total - attributionHealth.any_tagged} of {attributionHealth.total} leads have NO UTM / campaign / click ID — ads can&apos;t optimise for these.
                </div>
              </div>
              {attributionHealth.tagged_percent < 60 && (
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 border border-amber-500 text-amber-700 bg-amber-50">
                  Needs attention
                </span>
              )}
            </div>
            <div className="h-2.5 w-full bg-secondary rounded overflow-hidden">
              <div style={{ width: `${attributionHealth.tagged_percent}%` }} className="h-full bg-primary" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <AttrPill label="UTM" pct={attributionHealth.utm_percent} count={attributionHealth.with_utm} />
              <AttrPill label="gclid" pct={attributionHealth.gclid_percent} count={attributionHealth.with_gclid} color="#4285F4" />
              <AttrPill label="fbclid" pct={attributionHealth.fbclid_percent} count={attributionHealth.with_fbclid} color="#1877F2" />
              <AttrPill label="Campaign linked" pct={attributionHealth.campaign_percent} count={attributionHealth.with_campaign} />
            </div>
          </div>
        </div>
      )}

      {/* Attribution coverage by source — pinpoints campaigns dropping UTMs */}
      {attrCoverage && attrCoverage.rows && attrCoverage.rows.length > 0 && (
        <div className="px-8 pb-6" data-testid="attribution-coverage-widget">
          <div className="border border-border bg-white p-5">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Attribution coverage · per source</div>
                <h3 className="font-display text-lg font-semibold tracking-tight">
                  Which campaigns are dropping UTMs?
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Full attribution = utm_source + utm_medium + utm_campaign all present. Red rows below 40% need immediate landing-page fixes.
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {attrCoverage.totals.full_pct}% overall
              </span>
            </div>
            <div className="overflow-x-auto -mx-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground border-b border-border">
                    <th className="text-left px-5 py-2">Source</th>
                    <th className="text-right px-3 py-2 tabular-nums">Leads</th>
                    <th className="text-right px-3 py-2 tabular-nums" title="Any UTM / gclid / fbclid / campaign present">Any tag</th>
                    <th className="text-right px-3 py-2 tabular-nums" title="utm_source + utm_medium + utm_campaign">Full UTM</th>
                    <th className="text-left px-3 py-2 w-1/3">Coverage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {attrCoverage.rows.map((r) => {
                    const c = r.full_pct;
                    const barColor = c >= 80 ? "#008A00" : c >= 40 ? "#F5A623" : "#E00000";
                    const badge = c >= 80 ? "Healthy" : c >= 40 ? "Partial" : "Broken";
                    return (
                      <tr key={r.source_id} data-testid={`coverage-row-${r.source_id}`}
                          className="hover:bg-secondary/40">
                        <td className="px-5 py-2.5">
                          <div className="font-medium text-sm">{r.source_label}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{r.source_id}</div>
                        </td>
                        <td className="text-right px-3 py-2.5 tabular-nums">{r.total.toLocaleString()}</td>
                        <td className="text-right px-3 py-2.5 tabular-nums text-muted-foreground">
                          {r.with_any.toLocaleString()} <span className="text-[10px]">({r.any_pct}%)</span>
                        </td>
                        <td className="text-right px-3 py-2.5 tabular-nums">
                          <span className="font-semibold">{r.with_full.toLocaleString()}</span>
                          <span className="text-[10px] text-muted-foreground ml-1">({r.full_pct}%)</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-secondary rounded overflow-hidden">
                              <div style={{ width: `${c}%`, backgroundColor: barColor }} className="h-full transition-all" />
                            </div>
                            <span
                              style={{ borderColor: barColor, color: barColor }}
                              className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 border shrink-0"
                              data-testid={`coverage-badge-${r.source_id}`}
                            >
                              {badge}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Open Pipeline Breakdown */}
      {analytics?.open_by_status && (
        <div className="px-8 pb-6">
          <div className="border border-border bg-white p-5" data-testid="open-pipeline-breakdown">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <h3 className="font-display text-lg font-semibold tracking-tight">Open pipeline · by status</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Workable leads only · excludes converted, booked, existing client, lost, not interested, switch off, out of location
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Where leads are stuck</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {STATUSES.filter((s) => (analytics.open_by_status?.[s.id] || 0) > 0)
                .sort((a, b) => (analytics.open_by_status[b.id] || 0) - (analytics.open_by_status[a.id] || 0))
                .map((s) => {
                  const count = analytics.open_by_status[s.id] || 0;
                  const pct = analytics.open ? Math.round((count / analytics.open) * 100) : 0;
                  return (
                    <div
                      key={s.id}
                      data-testid={`open-status-${s.id}`}
                      className="border border-border p-3 hover:bg-secondary/40 transition-colors"
                      style={{ borderLeft: `3px solid ${s.color}` }}
                    >
                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground truncate">
                        {s.label}
                      </div>
                      <div className="font-display text-2xl font-bold tracking-tight mt-1 tabular-nums">
                        {count.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{pct}% of open</div>
                    </div>
                  );
                })}
              {STATUSES.filter((s) => (analytics.open_by_status?.[s.id] || 0) > 0).length === 0 && (
                <div className="col-span-full text-sm text-muted-foreground py-6 text-center">
                  No open leads. Pipeline is clear.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="px-8 pb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border bg-white p-5 lg:col-span-2">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display text-lg font-semibold tracking-tight">Leads by source</h3>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bar · Count</span>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={sourceData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "#F4F4F5" }}
                  contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 2, fontSize: 12 }}
                  labelFormatter={(_, p) => p?.[0]?.payload?.full || ""}
                />
                <Bar dataKey="value" fill="#002FA7" radius={[0, 0, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border bg-white p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display text-lg font-semibold tracking-tight">Status mix</h3>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pie</span>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            {statusData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                No leads yet
              </div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={90} stroke="#fff">
                    {statusData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 2, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="px-8 pb-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border bg-white p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display text-lg font-semibold tracking-tight">Branch performance</h3>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Unit-wise</span>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={branchData} layout="vertical" margin={{ top: 0, right: 16, left: 24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E4E4E7" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#A1A1AA" width={100} />
                <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="value" fill="#F5A623" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border bg-white p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display text-lg font-semibold tracking-tight">Agent leaderboard</h3>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Agent-wise</span>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={agentData} layout="vertical" margin={{ top: 0, right: 16, left: 24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E4E4E7" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#A1A1AA" width={100} />
                <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="value" fill="#002FA7" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Source breakdown table */}
      <div className="px-8 pb-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border bg-white lg:col-span-2">
          <div className="px-5 py-4 border-b border-border flex items-baseline justify-between">
            <h3 className="font-display text-lg font-semibold tracking-tight">Source × Status matrix</h3>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Live counts</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums" data-testid="source-status-matrix">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                  <th className="px-5 py-3 font-bold">Source</th>
                  {STATUSES.map((s) => (
                    <th key={s.id} className="px-3 py-3 font-bold text-right whitespace-nowrap">{s.label}</th>
                  ))}
                  <th className="px-5 py-3 font-bold text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {SOURCES.map((s) => {
                  const row = analytics?.source_status?.[s.id] || {};
                  const total = analytics?.by_source?.[s.id] || 0;
                  return (
                    <tr key={s.id} className="border-b border-border last:border-0 hover:bg-secondary/40">
                      <td className="px-5 py-3 font-medium whitespace-nowrap">{s.label}</td>
                      {STATUSES.map((st) => {
                        const v = row[st.id] || 0;
                        return (
                          <td key={st.id} className="px-3 py-3 text-right">
                            <span style={{ color: v > 0 ? st.color : "#A1A1AA" }} className={v > 0 ? "font-semibold" : ""}>
                              {v || "—"}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-5 py-3 text-right font-bold">{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-border bg-white p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display text-lg font-semibold tracking-tight">Trend · 14 days</h3>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Time-series</span>
          </div>
          <div style={{ width: "100%", height: 280 }} data-testid="time-series-chart">
            <ResponsiveContainer>
              <AreaChart data={analytics?.time_series || []} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#002FA7" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#002FA7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 2" stroke="#E4E4E7" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }}
                  stroke="#A1A1AA"
                  tickFormatter={(d) => d?.slice(5)}
                />
                <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" allowDecimals={false} />
                <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 2, fontSize: 12 }} />
                <Area type="monotone" dataKey="total" stroke="#002FA7" strokeWidth={2} fill="url(#totalGrad)" name="Leads" />
                <Line type="monotone" dataKey="converted" stroke="#008A00" strokeWidth={2} dot={false} name="Converted" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function AttrPill({ label, pct, count, color }) {
  return (
    <div className="border border-border p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span
          className="text-xl font-bold tabular-nums"
          style={color ? { color } : {}}
        >
          {pct}%
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums">({count})</span>
      </div>
    </div>
  );
}

